import { useState } from 'react';
import { trpc } from '../lib/trpc.js';
import { paraApi, useFiltro } from '../estado/filtros.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { SerieMensal, type PontoSerie } from '../componentes/SerieMensal.js';
import { inteiro, moeda, moedaOuTraco, participacao } from '../lib/formato.js';

/** O que a série mensal mede. Tributo e retenção não somam a mesma coisa. */
const MEDIDAS = [
  { chave: 'valorTotal', rotulo: 'Valor dos documentos' },
  { chave: 'totalTributos', rotulo: 'Tributos destacados' },
  { chave: 'totalRetencoes', rotulo: 'Retenções na fonte' },
] as const;

type Medida = (typeof MEDIDAS)[number]['chave'];

export function Analise() {
  const { filtro, definir } = useFiltro();
  const entrada = { filtro: paraApi(filtro) };
  const [medida, setMedida] = useState<Medida>('valorTotal');

  const evolucao = trpc.analise.evolucao.useQuery(entrada);
  const comparativo = trpc.analise.emitidasTomadas.useQuery(entrada);
  const obras = trpc.analise.porObra.useQuery({ ...entrada, limite: 25 });
  const contrapartes = trpc.analise.porContraparte.useQuery({ ...entrada, limite: 25 });

  if (evolucao.isPending || comparativo.isPending) {
    return (
      <section className="panel">
        <Carregando linhas={8} rotulo="Montando a análise" />
      </section>
    );
  }
  if (evolucao.error) {
    return (
      <section className="panel">
        <Erro erro={evolucao.error} aoTentarDeNovo={() => void evolucao.refetch()} />
      </section>
    );
  }

  const comp = comparativo.data;
  const totalDocs = (comp?.emitidas.documentos ?? 0) + (comp?.tomadas.documentos ?? 0);

  if (totalDocs === 0) {
    return (
      <section className="panel">
        <Vazio titulo="Nada para analisar nesta seleção">
          Envie XMLs na área acima, ou afrouxe os filtros — inclusive o recorte de emitidas e tomadas.
        </Vazio>
      </section>
    );
  }

  const pontos: PontoSerie[] = (evolucao.data?.serie ?? []).map((p) => ({
    competencia: p.competencia,
    emitidas: p.emitidas[medida],
    tomadas: p.tomadas[medida],
  }));
  const omitidos = evolucao.data?.mesesOmitidos ?? 0;

  const linhasComparativo = [
    { rotulo: 'Documentos', em: comp!.emitidas.documentos, to: comp!.tomadas.documentos, contagem: true },
    { rotulo: 'Valor', em: comp!.emitidas.valorTotal, to: comp!.tomadas.valorTotal },
    { rotulo: 'ICMS', em: comp!.emitidas.icms, to: comp!.tomadas.icms },
    { rotulo: 'IPI', em: comp!.emitidas.ipi, to: comp!.tomadas.ipi },
    { rotulo: 'PIS', em: comp!.emitidas.pis, to: comp!.tomadas.pis },
    { rotulo: 'COFINS', em: comp!.emitidas.cofins, to: comp!.tomadas.cofins },
    { rotulo: 'ISS', em: comp!.emitidas.iss, to: comp!.tomadas.iss },
    { rotulo: 'IBS', em: comp!.emitidas.ibs, to: comp!.tomadas.ibs, rtc: true },
    { rotulo: 'CBS', em: comp!.emitidas.cbs, to: comp!.tomadas.cbs, rtc: true },
    { rotulo: 'ISS retido', em: comp!.emitidas.retIss, to: comp!.tomadas.retIss, ret: true },
    { rotulo: 'INSS retido', em: comp!.emitidas.retInss, to: comp!.tomadas.retInss, ret: true },
    { rotulo: 'IRRF', em: comp!.emitidas.retIrrf, to: comp!.tomadas.retIrrf, ret: true },
    { rotulo: 'CSLL', em: comp!.emitidas.retCsll, to: comp!.tomadas.retCsll, ret: true },
  ];

  return (
    <>
      <section className="panel">
        <div className="toolbar">
          <h2 className="eyebrow">Evolução mensal</h2>
          <div className="medidas">
            {MEDIDAS.map((m) => (
              <button
                key={m.chave}
                type="button"
                className="chip"
                aria-pressed={medida === m.chave}
                onClick={() => setMedida(m.chave)}
              >
                {m.rotulo}
              </button>
            ))}
          </div>
        </div>
        <SerieMensal pontos={pontos} medida={MEDIDAS.find((m) => m.chave === medida)!.rotulo} />
        <p className="foot">
          Meses sem nota aparecem zerados de propósito: um buraco no eixo faria a queda parecer
          continuidade, e é justamente o mês vazio que costuma interessar.
          {omitidos > 0 ? (
            <>
              {' '}
              <strong>
                A janela mostra os últimos 24 meses; há {inteiro(omitidos)} competência(s) mais
                antiga(s) fora dela
                {evolucao.data?.primeiraOmitida ? `, a partir de ${evolucao.data.primeiraOmitida}` : ''}.
              </strong>{' '}
              Use o filtro de data para alcançá-las.
            </>
          ) : null}
        </p>
      </section>

      <section className="panel">
        <h2 className="eyebrow">Emitidas × tomadas</h2>
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th className="num">Emitidas</th>
                <th className="num">Tomadas</th>
                <th className="num">Diferença</th>
              </tr>
            </thead>
            <tbody>
              {linhasComparativo.map((l) => {
                const fmt = l.contagem ? inteiro : moedaOuTraco;
                const zerada = !l.contagem && Math.abs(l.em) < 0.005 && Math.abs(l.to) < 0.005;
                if (zerada) return null;
                return (
                  <tr key={l.rotulo}>
                    <td className={l.rtc ? 'strong rtc-ink' : 'strong'}>{l.rotulo}</td>
                    <td className="num">{fmt(l.em)}</td>
                    <td className="num">{fmt(l.to)}</td>
                    <td className="num muted">{fmt(l.em - l.to)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="foot">
          O que a empresa emitiu é receita; o que ela tomou é custo. A coluna de diferença é
          aritmética simples entre os dois lados — não é apuração de imposto a pagar.
        </p>
      </section>

      <section className="panel">
        <h2 className="eyebrow">Obras (CNO)</h2>
        {obras.isPending ? (
          <Carregando linhas={4} rotulo="Somando por obra" />
        ) : (obras.data ?? []).length === 0 ? (
          <Vazio titulo="Nenhum documento com CNO nesta seleção">
            Só entram aqui as notas em que a obra foi identificada, por campo do leiaute ou pelo
            texto complementar.
          </Vazio>
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>CNO</th>
                  <th>Descrição</th>
                  <th className="num">Docs</th>
                  <th className="num">Emit.</th>
                  <th className="num">Tom.</th>
                  <th className="num">Valor</th>
                  <th className="num">Tributos</th>
                  <th className="num">Retenções</th>
                  <th className="num">INSS ret.</th>
                </tr>
              </thead>
              <tbody>
                {(obras.data ?? []).map((o) => (
                  <tr key={o.cno}>
                    <td>
                      <button
                        type="button"
                        className="linkish mono"
                        title="Filtrar tudo por esta obra"
                        onClick={() => definir({ cno: o.cno })}
                      >
                        {o.cnoFormatado}
                      </button>
                    </td>
                    <td className="trunc">{o.descricao ?? '—'}</td>
                    <td className="num">{inteiro(o.documentos)}</td>
                    <td className="num">{inteiro(o.emitidas)}</td>
                    <td className="num">{inteiro(o.tomadas)}</td>
                    <td className="num">{moeda(o.valorTotal)}</td>
                    <td className="num">{moedaOuTraco(o.totalTributos)}</td>
                    <td className="num">{moedaOuTraco(o.totalRetencoes)}</td>
                    <td className="num">{moedaOuTraco(o.retInss)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <h2 className="eyebrow">Contrapartes</h2>
        {contrapartes.isPending ? (
          <Carregando linhas={4} rotulo="Somando por CNPJ" />
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>CNPJ / CPF</th>
                  <th>Nome</th>
                  <th className="num">Docs</th>
                  <th className="num">Emit.</th>
                  <th className="num">Tom.</th>
                  <th className="num">Valor</th>
                  <th className="num">% do valor</th>
                  <th className="num">Retenções</th>
                </tr>
              </thead>
              <tbody>
                {(contrapartes.data ?? []).map((c) => {
                  const totalValor = (comp!.emitidas.valorTotal + comp!.tomadas.valorTotal) || 0;
                  return (
                    <tr key={c.doc}>
                      <td>
                        <button
                          type="button"
                          className="linkish mono"
                          title="Filtrar tudo por este CNPJ"
                          onClick={() => definir({ contraparte: c.doc })}
                        >
                          {c.docFormatado}
                        </button>
                      </td>
                      <td className="trunc">{c.nome || '—'}</td>
                      <td className="num">{inteiro(c.documentos)}</td>
                      <td className="num">{inteiro(c.emitidas)}</td>
                      <td className="num">{inteiro(c.tomadas)}</td>
                      <td className="num">{moeda(c.valorTotal)}</td>
                      <td className="num muted">{participacao(c.valorTotal, totalValor)}</td>
                      <td className="num">{moedaOuTraco(c.totalRetencoes)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="foot">
          A contraparte é o outro lado da nota: nas tomadas é o fornecedor, nas emitidas é o cliente.
          Clique num CNPJ para recortar todas as telas por ele.
        </p>
      </section>
    </>
  );
}
